import React, { useEffect, useState } from "react";
import URL from "../../../../Main/url";
import formatDateDDMMYYYY from "../../../../Main/formatDate";

const InvoiceA5Header = React.forwardRef((props, ref) => {
  const { values } = props;

  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 5;

  const calculateTotalPages = () => {
    if (!values?.details) return 1;
    return Math.ceil(values.details.length / itemsPerPage);
  };

  const getItemsForPage = (page) => {
    if (!values?.details) return [];
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return values.details.slice(startIndex, endIndex);
  };

  useEffect(() => {
    if (values?.details) {
      const pages = calculateTotalPages();
      setTotalPages(pages);
    }
  }, [values?.details]);

  const injectScopedStyles = () => {
    const style = document.createElement("style");
    style.innerHTML = `
      @page {
        size: A5 landscape;
        margin: 0% 5% 3% 5%;
      }
  
      @media print {
        .a5-header-section {
          position: fixed;
          top: 0%;
          left: 0%;
          right: 0%;
          background: white;
          z-index: 1000;
          page-break-inside: avoid;
        }
        
        .a5-table-header-section {
          position: fixed;
          top: 25%; 
          left: 0%;
          right: 0%;
          background: white;
          z-index: 999;
          page-break-inside: avoid;
        }
        
        .a5-invoice-content {
          margin-top: 30%; 
          left: 0%;
          right: 0%;
           margin-right:0.5cm;
        }
        
        .a5-page-break {
          page-break-before: always;
          margin-top: 30%;
        }
        
        .a5-landscape {
          page: a5-landscape;
        }

         .a5-totals {
            page-break-inside: avoid;
            page-break-before: always; 
            margin-top: 7cm;
            margin-right:0.5cm;
          }

          .invoice-table {
            width: 100%;
          }
          
          .col-sno     { width: 5%; }
          .col-desc    { width: 30%; }
          .col-batch   { width: 12%; }
          .col-expiry  { width: 10%; }
          .col-unit    { width: 6%; }
          .col-qty     { width: 10%; }
          .col-price   { width: 11%; }
          .col-vat     { width: 10%; }
          .col-total   { width: 14%; }

      }
    `;
    document.head.appendChild(style);
  };

  useEffect(() => {
    if (values?.branch?.name) {
      injectScopedStyles();
    }
  }, [values?.branch?.name]);

  const renderHeaderSection = () => (
    <div className="a5-header-section">
      <div className="text-black-900 text-[0.65rem] text-center">
        <div className="text-center">
          {values?.branch?.image ? (
            <div className="flex justify-center">
              <div className="text-xl pb-1">
                <img
                  className="w-[50px] h-[50px]"
                  src={`${URL}/uploads${values?.branch?.image}`}
                />
              </div>
            </div>
          ) : (
            ""
          )}
          <div className="text-medium text-xl">{values?.branch?.name}</div>
          <div className="text-medium">{values?.branch?.category}</div>
        </div>
      </div>
      <div className="text-black-900 text-[0.65rem] text-center pt-1">
        <span>
          Email&nbsp;:&nbsp;
          {values?.branch?.email ? values?.branch?.email + ", " : ""}
          &nbsp;
        </span>
        <span>
          Phone&nbsp;:&nbsp;
          {values?.branch?.phone ? values?.branch?.phone : ""}
          &nbsp;
        </span>
        <div>
          {values?.branch?.area ? values?.branch?.area + ", " : ""}
          {values?.branch?.city ? values?.branch?.city + ", " : ""}
          {values?.branch?.country ? values?.branch?.country : ""}
        </div>
        <div>VATIN&nbsp;:&nbsp;{values?.branch?.tax}</div>
      </div>

      <hr className="my-1 border-t-black-900 border-black" />
      <div className="text-medium text-lg text-center">TAX INVOICE</div>

      <div className="text-regular text-xs pb-4">
        <div className="flex justify-between">
          <div>
            <table>
              <tbody>
                <tr>
                  <td>Customer</td>
                  <td className="px-2">:</td>
                  <td className="text-right">
                    {values?.customer?.name
                      ? values?.customer?.name
                      : "Walk-in-customer"}
                  </td>
                </tr>
                <tr>
                  <td>Phone</td>
                  <td className="px-2">:</td>
                  <td className="text-right">
                    {values?.customer?.phone ? values?.customer?.phone : ""}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <table>
              <tbody>
                <tr>
                  <td>Invoice No</td>
                  <td className="px-2">:</td>
                  <td className="text-right">#INV-{values?.number}</td>
                </tr>
                <tr>
                  <td>Date</td>
                  <td className="px-2">:</td>
                  <td className="text-right">
                    {formatDateDDMMYYYY?.(values?.date)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTableHeaders = () => (
    <div className="a5-table-header-section">
      <table className="invoice-table" style={styles.table}>
        <thead>
          <tr style={styles.tableHeader}>
            <th
              className="col-sno text-center pl-2 text-xs text-regular"
              style={styles.tableCell}
            >
              S.No
            </th>
            <th
              className="col-desc text-center text-xs text-regular"
              style={styles.tableCell}
            >
              Description
            </th>
            <th
              className="col-batch text-center text-xs text-regular"
              style={styles.tableCell}
            >
              Batch
            </th>
            <th
              className="col-expiry text-center text-xs text-regular"
              style={styles.tableCell}
            >
              Expiry
            </th>
            <th
              className="col-unit text-center text-xs text-regular"
              style={styles.tableCell}
            >
              Unit
            </th>
            <th
              className="col-qty text-center text-xs text-regular"
              style={styles.tableCell}
            >
              Quantity
            </th>
            <th
              className="col-price text-center text-xs text-regular"
              style={styles.tableCell}
            >
              Price
            </th>
            <th
              className="col-vat  text-center text-xs text-regular"
              style={styles.tableCell}
            >
              Tax
            </th>
            <th
              className="col-total text-right pr-2 text-xs text-regular"
              style={styles.tableCell}
            >
              Total
            </th>
          </tr>
        </thead>
      </table>
    </div>
  );

  const renderTableBody = (pageItems, pageNumber, isLastPage) => (
    <div
      key={`page-${pageNumber}`}
      className={pageNumber > 1 ? "a5-page-break" : "a5-invoice-content"}
    >
      <table className="invoice-table" style={styles.table}>
        <tbody className="text-regular">
          {pageItems.map((item, index) => {
            const globalIndex = (pageNumber - 1) * itemsPerPage + index;
            return (
              <tr key={index}>
                <td className="col-sno text-left pl-2 text-xs" style={styles.tableCell}>
                  <div>{globalIndex + 1}</div>
                </td>
                <td className="col-desc text-left text-xs" style={styles.tableCell}>
                  <div>{item?.name}</div>
                </td>
                <td className="col-batch text-left text-xs" style={styles.tableCell}>
                  <div>
                    {item?.description?.number
                      ? `${item?.description?.number}`
                      : ""}
                  </div>
                </td>
                <td className="col-expiry text-left text-xs" style={styles.tableCell}>
                  <div>
                    {item?.description?.expiry_date
                      ? `${formatDateDDMMYYYY?.(item?.description?.expiry_date)}`
                      : ""}
                  </div>
                </td>
                <td className="col-unit text-left text-xs" style={styles.tableCell}>
                  {item?.unit_name}
                </td>
                <td className="col-qty text-left text-xs" style={styles.tableCell}>
                  {item?.quantity}
                </td>
                <td className="col-price text-left text-xs" style={styles.tableCell}>
                  {item?.sale_price?.toFixed(3)}
                </td>
                <td className="col-vat text-left text-xs" style={styles.tableCell}>
                  {item?.tax_amount?.toFixed(3)}
                </td>
                <td
                  className="col-total text-left text-right pr-2 text-xs"
                  style={styles.tableCell}
                >
                  {item?.total?.toFixed(3)}
                </td>
              </tr>
            );
          })}

          {pageItems.length < itemsPerPage &&
            Array.from({ length: itemsPerPage - pageItems.length }).map((_, index) => (
              <tr key={`empty-${pageNumber}-${index}`}>
                <td className="text-left pl-2 text-xs" style={styles.tableCell}>
                  &nbsp;
                </td>
                <td className="text-left text-xs" style={styles.tableCell}>&nbsp;</td>
                <td className="text-left text-xs" style={styles.tableCell}>&nbsp;</td>
                <td className="text-left text-xs" style={styles.tableCell}>&nbsp;</td>
                <td className="text-left text-xs" style={styles.tableCell}>&nbsp;</td>
                <td className="text-left text-xs" style={styles.tableCell}>&nbsp;</td>
                <td className="text-left text-xs" style={styles.tableCell}>&nbsp;</td>
                <td className="text-left text-xs" style={styles.tableCell}>&nbsp;</td>
                <td className="text-left text-right pr-2 text-xs" style={styles.tableCell}>&nbsp;</td>
              </tr>
            ))
          }
        </tbody>
      </table>

      {isLastPage && (
        <div className="mt-2 a5-totals">
          <table className="invoice-table" style={styles.table}>
            <tbody className="text-regular">
              <tr style={styles.totalRow}>
                <td
                  className="text-regular text-gray-700 text-medium text-xs"
                  style={styles.tableCell}
                  colSpan="8"
                  align="right"
                >
                  Subtotal
                </td>
                <td className="text-right pr-2" style={styles.tableCell}>
                  {values?.subtotal?.toFixed(3)}
                </td>
              </tr>
              <tr style={styles.totalRow}>
                <td
                  className="text-regular text-gray-700 text-medium text-xs"
                  style={styles.tableCell}
                  colSpan="8"
                  align="right"
                >
                  Tax [VAT]
                </td>
                <td className="text-right pr-2" style={styles.tableCell}>
                  {values?.taxamount?.toFixed(3)}
                </td>
              </tr>
              <tr style={styles.totalRow}>
                <td
                  className="text-regular text-gray-700 text-medium text-xs"
                  style={styles.tableCell}
                  colSpan="8"
                  align="right"
                >
                  Discount
                </td>
                <td className="text-right pr-2" style={styles.tableCell}>
                  {values?.discount?.toFixed(3)}
                </td>
              </tr>
              <tr style={styles.totalRow}>
                <td
                  className="text-regular text-medium text-xs bg-[#f0f0f0]"
                  style={styles.tableCell}
                  colSpan="8"
                  align="right"
                >
                  Total
                </td>
                <td
                  className="text-right pr-2 text-regular text-medium text-xs bg-[#f0f0f0]"
                  style={styles.tableCell}
                >
                  {values?.total?.toFixed(3)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="a5-landscape" ref={ref} style={styles.container}>
      <style>
        {`
          @page a5-landscape {
            size: A5 landscape;
            margin-top: 0.5cm;
            margin-bottom: 2cm;
            margin-left: 0.5cm;
            margin-right: 0.5cm;
          }
          
          @media print {
            .a5-landscape {
              width: 100%;
              height: 100%;
              page-break-inside: avoid;
            }

            .a5-landscape + * {
              display: none !important;
            }
            
            .a5-header-section {
              position: fixed;
              top: 0cm;
              left: 0.5cm;
              right: 0.5cm;
              background: white;
              z-index: 1000;
              page-break-inside: avoid;
            }
            
            .a5-table-header-section {
              position: fixed;
              top: 6cm;
              left: 0.5cm;
              right: 0.5cm;
              background: white;
              z-index: 999;
              page-break-inside: avoid;
            }
            
            .a5-invoice-content {
              margin-top: 7cm;
              margin-left: 0.5cm
            }
            
            .a5-page-break {
              page-break-before: always;
              margin-top: 7cm;
              margin-left: 0.5cm
            }
            
            .a5-landscape {
              page: a5-landscape;
            }
          }
          
          @media screen {
            .a5-header-section {
              position: static;
            }
            
            .a5-table-header-section {
              position: static;
            }
            
            .a5-invoice-content {
              margin-top: 0;
            }
            
            .a5-page-break {
              margin-top: 0;
              page-break-before: auto;
            }
           
            .invoice-table {
              width: 100%;
            }
            
            .col-sno     { width: 5%; }
            .col-desc    { width: 30%; }
            .col-batch   { width: 12%; }
            .col-expiry  { width: 10%; }
            .col-unit    { width: 6%; }
            .col-qty     { width: 10%; }
            .col-price   { width: 11%; }
            .col-vat     { width: 10%; }
            .col-total   { width: 14%; }
          }
        `}
      </style>

      {renderHeaderSection()}

      {renderTableHeaders()}

      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => {
        const pageItems = getItemsForPage(pageNumber);
        const isLastPage = pageNumber === totalPages;
        return renderTableBody(pageItems, pageNumber, isLastPage);
      })}
    </div>
  );
});

const styles = {
  container: {
    width: "100%",
    height: "100%",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
  },
  tableCell: {
    padding: "8px",
    textAlign: "left",
  },
  totalRow: {
    fontWeight: "normal",
  },
};

export default InvoiceA5Header;